import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateProfile } from '../../api/account.ts';
import { createPairingCode } from '../../api/auth.ts';
import { useAuth } from '../../store/auth.ts';
import { useUi, type Theme } from '../../store/ui.ts';
import { initialsOf } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { Modal } from '../common/Modal.tsx';

const THEMES: Array<{ value: Theme; label: string }> = [
  { value: 'system', label: '시스템 설정' },
  { value: 'light', label: '밝게' },
  { value: 'dark', label: '어둡게' },
];

export function ProfileTab() {
  const user = useAuth((s) => s.user);
  const bootstrap = useAuth((s) => s.bootstrap);
  const theme = useUi((s) => s.theme);
  const setTheme = useUi((s) => s.setTheme);
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.display_name ?? '');
  const [busy, setBusy] = useState(false);
  const [pairing, setPairing] = useState<{ code: string; expires_at: string } | null>(null);

  const dirty = name.trim() !== user?.display_name && name.trim().length > 0;

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await updateProfile(name.trim());
      await bootstrap();
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('프로필을 저장했습니다');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const pair = async (): Promise<void> => {
    try {
      setPairing(await createPairingCode());
    } catch (e) { toast.error(e); }
  };

  return (
    <>
      <section className="panel">
        <h2>프로필</h2>
        <p className="panel__hint">이름은 공유 폴더 참여자와 파일 수정 기록에 표시됩니다.</p>

        <div className="row" style={{ marginBottom: 16 }}>
          <div className="avatar" style={{ width: 56, height: 56, fontSize: 20 }}>
            {initialsOf(user?.display_name ?? '')}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{user?.display_name}</div>
            <div className="faint" style={{ fontSize: 13 }}>{user?.email}</div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="profile-name">표시 이름</label>
          <input
            id="profile-name" className="input" maxLength={60}
            value={name} onChange={(e) => { setName(e.target.value); }}
          />
        </div>
        <button
          className="btn btn--primary" disabled={!dirty || busy}
          onClick={() => { void save(); }}
        >
          {busy ? <span className="spinner" /> : '저장'}
        </button>
      </section>

      <section className="panel">
        <h2>화면</h2>
        <p className="panel__hint">이 브라우저에만 적용됩니다.</p>
        <div className="row">
          {THEMES.map((t) => (
            <button
              key={t.value}
              className={theme === t.value ? 'btn btn--primary' : 'btn'}
              onClick={() => { setTheme(t.value); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="panel">
        <h2>PC 연결</h2>
        <p className="panel__hint">
          PC 클라이언트를 설치한 뒤 아래에서 발급한 8자리 코드를 입력하면 계정과 연결됩니다.
        </p>
        <div className="row">
          <button className="btn btn--primary" onClick={() => { void pair(); }}>
            연결 코드 발급
          </button>
          <a className="btn" href="/download">클라이언트 내려받기</a>
        </div>
      </section>

      {pairing && (
        <Modal title="PC 연결 코드" onClose={() => { setPairing(null); }}>
          <p className="modal__text">
            PC 클라이언트에서 <code>yumsync -pair &lt;코드&gt;</code> 로 입력하세요. 10분 뒤 만료됩니다.
          </p>
          <div
            className="mono"
            style={{
              fontSize: 30, letterSpacing: '.18em', textAlign: 'center',
              padding: 'var(--sp-4)', background: 'var(--c-bg-sunken)',
              borderRadius: 'var(--r-md)',
            }}
          >
            {pairing.code}
          </div>
        </Modal>
      )}
    </>
  );
}
